import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import { isMedia } from '@shared/lib/html';
import { Avatar } from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import { Label } from '@shared/ui/label';
import IconChevronLeft from '@assets/img/icons/chevron-left.svg';
import IconChevronRight from '@assets/img/icons/chevron-right.svg';

import { isParticipantSendingMedia } from '../../lib/misc';
import {
	GRID_GAP,
	GRID_PADDING_CONTROLS,
	GRID_PADDING_CONTROLS_MOBILE,
	GRID_PADDING_DEFAULT,
	TILE_ASPECT_RATIO,
	TILE_MIN_HEIGHT,
	TILE_MIN_WIDTH,
} from './constants';
import type { ConferenceGridProps, GridLayout, GridPadding, GridPoint } from './types';

import styles from './styles.module.scss';

const computeBestLayout = (
	count: number,
	viewportWidth: number,
	viewportHeight: number,
	padding: GridPadding
): GridLayout | null => {
	if (!count || viewportWidth <= 0 || viewportHeight <= 0) return null;

	const innerWidth = Math.max(0, viewportWidth - padding.left - padding.right);
	const innerHeight = Math.max(0, viewportHeight - padding.top - padding.bottom);
	if (!innerWidth || !innerHeight) return null;

	let best: {
		cols: number;
		rows: number;
		tileWidth: number;
		tileHeight: number;
		score: number;
		fitsMinSize: boolean;
	} | null = null;

	for (let cols = 1; cols <= count; cols += 1) {
		const rows = Math.ceil(count / cols);
		const gapWidth = (cols - 1) * GRID_GAP;
		const gapHeight = (rows - 1) * GRID_GAP;
		const widthForTiles = innerWidth - gapWidth;
		const heightForTiles = innerHeight - gapHeight;
		if (widthForTiles <= 0 || heightForTiles <= 0) continue;

		let tileWidth = widthForTiles / cols;
		let tileHeight = tileWidth / TILE_ASPECT_RATIO;
		const projectedHeight = tileHeight * rows;

		if (projectedHeight > heightForTiles) {
			tileHeight = heightForTiles / rows;
			tileWidth = tileHeight * TILE_ASPECT_RATIO;
		}

		const fitsMinSize = tileWidth >= TILE_MIN_WIDTH && tileHeight >= TILE_MIN_HEIGHT;
		const area = tileWidth * tileHeight;
		const ratioPenalty = Math.abs(tileWidth / tileHeight - TILE_ASPECT_RATIO);
		const score = area - ratioPenalty * 800 + (fitsMinSize ? 1_000_000 : 0) - rows * 25;

		if (!best || score > best.score) {
			best = { cols, rows, tileWidth, tileHeight, score, fitsMinSize };
		}
	}

	if (!best) return null;

	const rowsCount = Math.max(1, Math.ceil(count / best.cols));
	const contentHeight = rowsCount * best.tileHeight + (rowsCount - 1) * GRID_GAP;
	const originY = padding.top + Math.max(0, (innerHeight - contentHeight) / 2);

	const positions: GridPoint[] = Array.from({ length: count }, (_, index) => {
		const row = Math.floor(index / best.cols);
		const col = index % best.cols;
		const remaining = count - row * best.cols;
		const itemsInRow = Math.max(1, Math.min(best.cols, remaining));
		const rowWidth = itemsInRow * best.tileWidth + Math.max(0, itemsInRow - 1) * GRID_GAP;
		const originX = padding.left + Math.max(0, (innerWidth - rowWidth) / 2);

		return {
			x: originX + col * (best.tileWidth + GRID_GAP),
			y: originY + row * (best.tileHeight + GRID_GAP),
		};
	});

	return {
		cols: best.cols,
		rows: rowsCount,
		tileWidth: best.tileWidth,
		tileHeight: best.tileHeight,
		positions,
		fitsMinSize: best.fitsMinSize,
	};
};

export const ConferenceGrid: FC<ConferenceGridProps> = ({ participants, renderLabelContent }) => {
	const gridRef = useRef<HTMLDivElement | null>(null);
	const [pageIndex, setPageIndex] = useState(0);
	const [gridSize, setGridSize] = useState<{ width: number; height: number }>({
		width: 0,
		height: 0,
	});
	const isMobile = isMedia('xs');

	useEffect(() => {
		const node = gridRef.current;
		if (!node) return;

		const updateSize = () =>
			setGridSize({
				width: node.clientWidth,
				height: node.clientHeight,
			});

		updateSize();

		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateSize);

			return () => window.removeEventListener('resize', updateSize);
		}

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			const width = entry?.contentRect?.width || node.clientWidth;
			const height = entry?.contentRect?.height || node.clientHeight;

			setGridSize({ width, height });
		});

		observer.observe(node);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		setPageIndex(0);
	}, [participants.length]);

	const calculateTilesPerPage = useCallback(
		(padding: GridPadding) => {
			if (participants.length === 0) return 0;
			if (!gridSize.width || !gridSize.height) return Math.max(1, participants.length);

			for (let items = participants.length; items >= 1; items -= 1) {
				const layout = computeBestLayout(items, gridSize.width, gridSize.height, padding);
				if (!layout) continue;

				if (layout.fitsMinSize) {
					return items;
				}
			}

			return 1;
		},
		[gridSize.height, gridSize.width, participants.length]
	);

	const tilesWithoutControls = useMemo(
		() => calculateTilesPerPage(GRID_PADDING_DEFAULT),
		[calculateTilesPerPage]
	);

	const hasControls = Boolean(tilesWithoutControls && participants.length > tilesWithoutControls);
	const controlsPadding = isMobile ? GRID_PADDING_CONTROLS_MOBILE : GRID_PADDING_CONTROLS;
	const gridPadding = hasControls ? controlsPadding : GRID_PADDING_DEFAULT;

	const tilesPerPage = useMemo(
		() => calculateTilesPerPage(gridPadding),
		[calculateTilesPerPage, gridPadding]
	);

	const totalPages = tilesPerPage ? Math.ceil(participants.length / tilesPerPage) : 0;
	const safePageIndex = Math.min(pageIndex, Math.max(totalPages - 1, 0));
	const hasPagination = totalPages > 1;

	useEffect(() => {
		if (!totalPages) return;

		setPageIndex((prev) => Math.min(prev, Math.max(totalPages - 1, 0)));
	}, [totalPages]);

	const pageStart = safePageIndex * (tilesPerPage || 1);
	const pageParticipants = participants.slice(pageStart, pageStart + (tilesPerPage || 1));
	const pageLayout = useMemo(
		() => computeBestLayout(pageParticipants.length, gridSize.width, gridSize.height, gridPadding),
		[gridPadding, gridSize.height, gridSize.width, pageParticipants.length]
	);
	const isLayoutReady = Boolean(pageLayout && gridSize.width && gridSize.height);

	const handleNextPage = () => setPageIndex((prev) => Math.min(prev + 1, totalPages - 1));
	const handlePrevPage = () => setPageIndex((prev) => Math.max(prev - 1, 0));

	return (
		<div className={styles.RemoteVideoContainer}>
			<div
				ref={gridRef}
				className={cn(styles.Grid, {
					[styles.GridCalculated]: isLayoutReady,
				})}
			>
				{pageParticipants.map((participant, index) => {
					const position = pageLayout?.positions[index];
					const tileStyle =
						position && pageLayout
							? {
									width: `${pageLayout.tileWidth}px`,
									height: `${pageLayout.tileHeight}px`,
									transform: `translate(${position.x}px, ${position.y}px)`,
								}
							: undefined;

					return (
						<div
							key={participant.id || `${participant.name}-${index}`}
							aria-label={participant.name}
							className={styles.Tile}
							style={tileStyle}
						>
							<Avatar name={participant.name} size={80} />
							<Label
								color="dark"
								tagClass={styles.TileFooter}
								text={renderLabelContent(
									participant.name,
									!isParticipantSendingMedia(participant, 'audio')
								)}
							/>
						</div>
					);
				})}
			</div>
			{hasPagination && (
				<div className={styles.GridControls}>
					<Button
						buttonType="icon"
						className={cn(styles.GridNavButton, styles.GridNavButtonPrev)}
						disabled={safePageIndex === 0}
						svg={IconChevronLeft}
						onClick={handlePrevPage}
					/>
					<div className={styles.GridCounter}>
						{safePageIndex + 1} / {totalPages}
					</div>
					<Button
						buttonType="icon"
						className={cn(styles.GridNavButton, styles.GridNavButtonNext)}
						disabled={safePageIndex >= totalPages - 1}
						svg={IconChevronRight}
						onClick={handleNextPage}
					/>
				</div>
			)}
		</div>
	);
};
